import { useState, type HTMLAttributes, type ReactNode } from 'react';
import { TONE_TEXT, type Tone } from './badgeStyles';
import { cn } from './cn';

export interface StatProps extends HTMLAttributes<HTMLDivElement> {
  label: ReactNode;
  value: ReactNode;
  /** Unit label after the value (project units are labels only). */
  unit?: ReactNode;
  /** Context under the value, e.g. the point with the largest residual. */
  sublabel?: ReactNode;
  /** Colors the value. Use status tones only for a statistical verdict. */
  tone?: Tone;
  /**
   * Identity used to detect a change. Defaults to `value` when it is a
   * string or number. A change plays a brief highlight behind the value.
   */
  changeKey?: string | number | null;
  /** `tile` adds a bordered surface; `plain` sits inside an existing card. */
  variant?: 'plain' | 'tile';
}

/** KPI: small uppercase label over a large monospace value. */
export const Stat = ({
  label,
  value,
  unit,
  sublabel,
  tone = 'neutral',
  changeKey,
  variant = 'plain',
  className,
  ...props
}: StatProps) => {
  const key =
    changeKey !== undefined
      ? changeKey
      : typeof value === 'string' || typeof value === 'number'
        ? value
        : null;
  // Derive the highlight during render (no effect) so the new value and its
  // highlight paint in the same frame. Remounting the value restarts the
  // animation on every subsequent change.
  const [previousKey, setPreviousKey] = useState(key);
  const [changes, setChanges] = useState(0);
  if (key !== previousKey) {
    setPreviousKey(key);
    setChanges((count) => count + 1);
  }

  return (
    <div
      className={cn(
        'flex min-w-0 flex-col gap-1',
        variant === 'tile' &&
          'rounded-xl border border-border bg-surface px-4 py-3 shadow-sm',
        className
      )}
      {...props}
    >
      <div className="truncate text-[11px] font-medium uppercase tracking-wide text-fg-muted">
        {label}
      </div>
      <div className="flex min-w-0 items-baseline gap-1.5">
        <span
          key={changes}
          data-highlight={changes > 0 ? 'true' : undefined}
          className={cn(
            '-mx-1 truncate rounded px-1 font-mono text-2xl font-medium tabular-nums tracking-tight',
            tone === 'neutral' ? 'text-fg' : TONE_TEXT[tone],
            changes > 0 && 'animate-value-highlight'
          )}
        >
          {value}
        </span>
        {unit != null && (
          <span className="shrink-0 text-sm text-fg-muted">{unit}</span>
        )}
      </div>
      {sublabel != null && (
        <div className="truncate text-xs text-fg-muted">{sublabel}</div>
      )}
    </div>
  );
};
